import { api } from "../lib";

export interface WorkListStainSubmission {
  expectedSpecimenNumber?: string;
  specimenNumber: string;
  stainId: string;
  stainCode: string;
  isCompleted: boolean;
}

export interface WorkListSpecimenRow {
  siteLetter: string;
  site: string;
  stain: string;
  blockCount: number;
}

export interface GroupedWorkListSpecimen {
  specimenNumber: string;
  site: string;
  blockCount: number;
  requestedStains: string[];
}

export interface ResolvedWorkListStain {
  specimenNumber: string;
  requestedStain: string;
  stainId: string;
  stainCode: string;
  stainType: string;
}

export interface UnresolvedWorkListStain {
  specimenNumber: string;
  requestedStain: string;
}

export interface WorkListExtractedCase {
  accession: string;
  patient: string;
  rows: WorkListSpecimenRow[];
}

export interface WorkList {
  id: string;
  practiceId: string;
  listDate: string;
  fileName: string;
  fileUrl: string | null;
  patientCount: number;
  casesCreated: number;
  caseCount: number;
  matchedCount: number;
  status: string;
  errorMessage: string | null;
  rawData: {
    extractedCases?: WorkListExtractedCase[];
    totalRows?: number;
  } | null;
  createdAt: string;
  practice?: { id: string; name: string };
}

export interface ExpectedCase {
  id: string;
  workListId: string;
  externalCaseNumber: string;
  patientFirstName: string;
  patientLastName: string;
  specimenCount: number;
  specimensData: WorkListSpecimenRow[];
  groupedSpecimens?: GroupedWorkListSpecimen[];
  resolvedStains?: ResolvedWorkListStain[];
  unresolvedStains?: UnresolvedWorkListStain[];
  status: string;
  linkedCaseId: string | null;
  matchedAt: string | null;
  createdAt: string;
}

export const workListService = {
  getAll(): Promise<WorkList[]> {
    return api.get<WorkList[]>("/work-lists");
  },

  getById(id: string): Promise<WorkList> {
    return api.get<WorkList>(`/work-lists/${id}`);
  },

  async upload(
    file: File,
    practiceId: string
  ): Promise<{ workListId: string }> {
    const fileBase64 = await fileToBase64(file);
    return api.post<{ workListId: string }>("/work-lists/upload", {
      fileBase64,
      fileName: file.name,
      practiceId,
    });
  },

  confirmWorkList(
    workListId: string,
    cases: WorkListExtractedCase[]
  ): Promise<ExpectedCase[]> {
    return api.post<ExpectedCase[]>(`/work-lists/${workListId}/confirm`, {
      cases,
    });
  },

  getExpectedCases(workListId: string): Promise<ExpectedCase[]> {
    return api.get<ExpectedCase[]>(`/work-lists/${workListId}/expected-cases`);
  },

  lookupExpectedCase(externalCaseNumber: string): Promise<ExpectedCase | null> {
    return api.get<ExpectedCase | null>(
      `/work-lists/expected-case-lookup?externalCaseNumber=${encodeURIComponent(
        externalCaseNumber
      )}`
    );
  },

  matchExpectedCase(
    expectedCaseId: string,
    caseId: string
  ): Promise<ExpectedCase> {
    return api.post<ExpectedCase>("/work-lists/match-expected-case", {
      expectedCaseId,
      caseId,
    });
  },
};

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
